/**
 * Les tarifs qu'on peut proposer pour corriger une inscription.
 *
 * ## Pourquoi ce module ne se contente pas de lire le catalogue
 *
 * Chaque session porte ses quatre prix : `prix_normal`, `prix_reduit`,
 * `prix_local`, `prix_local_reduit`. Il serait tentant de calculer l'écart de
 * tarif comme `prix_normal − prix_reduit` et d'en faire le montant de la note
 * de crédit.
 *
 * **Ce serait faux 204 fois sur 7 001.** Mesuré en production le 3 septembre
 * 2026 : le prix facturé sur l'inscription et le prix du catalogue de sa
 * session diffèrent pour 204 inscriptions, dans les deux sens, jusqu'à 65,00 €
 * d'écart. Les prix des sessions ont été modifiés après facturation, ou une
 * condition tarifaire les a modulés.
 *
 * D'où la règle tenue ici : **le catalogue propose, le montant facturé
 * décide**. L'écart crédité se calcule toujours depuis
 * `registrations.amount_paid` — ce que la famille a réellement reçu comme
 * facture — et jamais depuis le catalogue.
 *
 * Le catalogue sert à autre chose, et c'est précieux : il évite de taper un
 * montant. L'administration clique « tarif réduit » et le nouveau prix est
 * celui de la session, sans faute de frappe possible.
 *
 * ## Deux tarifs sur cinq n'existent pas partout
 *
 * 142 sessions sur 339 n'ont pas de tarif local : ce sont les centres hors
 * commune. Proposer « tarif local » sur ces sessions n'aurait aucun sens, donc
 * les options se construisent depuis ce que la session porte vraiment.
 */

/** Les quatre types de tarif, tels que `registrations.price_type` les nomme. */
export const PRICE_TYPES = ['normal', 'reduced', 'local', 'local_reduced'] as const;
export type PriceType = (typeof PRICE_TYPES)[number];

export const PRICE_TYPE_LABELS: Record<PriceType, string> = {
  normal: 'Tarif normal',
  reduced: 'Tarif réduit',
  local: 'Tarif local',
  local_reduced: 'Tarif local réduit',
};

/** Les quatre prix d'une session, en centimes. `null` quand le tarif n'existe pas. */
export interface SessionTariffs {
  normal: number | null;
  reduced: number | null;
  local: number | null;
  local_reduced: number | null;
}

/** Un tarif proposable pour une inscription. */
export interface TariffOption {
  priceType: PriceType;
  label: string;
  /** Le prix du catalogue de la session, en centimes. */
  amountCents: number;
  /** Ce que la note de crédit porterait : montant facturé moins ce tarif. */
  creditCents: number;
  /** Ce tarif est celui actuellement porté par l'inscription. */
  current: boolean;
}

/**
 * Normalise un `price_type` lu en base.
 *
 * Une ligne de production porte `'reduced\r\n'` — un retour chariot avalé lors
 * d'un import. Comparer sans découper laisserait cette inscription hors de
 * toute proposition, sans que rien ne le signale.
 */
export function normalizePriceType(raw: string | null | undefined): PriceType | null {
  const t = (raw ?? '').trim().toLowerCase();
  return (PRICE_TYPES as readonly string[]).includes(t) ? (t as PriceType) : null;
}

/**
 * Les tarifs qu'on peut proposer pour une inscription, et l'écart de chacun.
 *
 * Ne sont retenus que les tarifs **strictement inférieurs** au montant
 * facturé : une note de crédit ne peut que réduire. Un tarif absent de la
 * session est ignoré, et le tarif courant est marqué sans être écarté — le
 * voir dans la liste dit à l'administration d'où l'on part.
 *
 * Les options sont rendues du moins cher au plus cher, parce que la question
 * posée est « à combien faut-il descendre ».
 */
export function tariffOptions(
  billedAmountCents: number,
  currentPriceType: string | null | undefined,
  tariffs: SessionTariffs,
): TariffOption[] {
  const courant = normalizePriceType(currentPriceType);

  const options: TariffOption[] = [];
  for (const t of PRICE_TYPES) {
    const prix = tariffs[t];
    if (prix === null || prix === undefined) continue;
    options.push({
      priceType: t,
      label: PRICE_TYPE_LABELS[t],
      amountCents: prix,
      // L'ecart part TOUJOURS du montant facture, jamais du catalogue : sur
      // 204 inscriptions les deux diffèrent.
      creditCents: billedAmountCents - prix,
      current: t === courant,
    });
  }

  return options
    .filter((o) => o.creditCents > 0 || o.current)
    .sort((a, b) => a.amountCents - b.amountCents);
}

/**
 * Le prix du catalogue diffère-t-il de ce qui a été facturé ?
 *
 * Quand c'est le cas, l'écran doit le dire : l'administration croirait
 * autrement que le tarif affiché est celui de la facture. 204 inscriptions
 * sont dans ce cas en production, avec des écarts allant de −44,00 € à
 * +65,00 €.
 */
export function billedDiffersFromCatalogue(
  billedAmountCents: number,
  currentPriceType: string | null | undefined,
  tariffs: SessionTariffs,
): { differs: boolean; catalogueCents: number | null } {
  const courant = normalizePriceType(currentPriceType);
  const catalogue = courant ? tariffs[courant] : null;
  if (catalogue === null || catalogue === undefined) return { differs: false, catalogueCents: null };
  return { differs: catalogue !== billedAmountCents, catalogueCents: catalogue };
}
